import React from 'react';
import { FaFacebookF, FaXTwitter, FaInstagram, FaLinkedinIn, FaTiktok } from 'react-icons/fa6';

const LEGAL_LINKS = [
  { label: 'Privacy Policy', href: 'https://symplelending.com/privacy-policy' },
  { label: 'Terms of Use', href: 'https://symplelending.com/terms-of-use' },
  { label: 'CA Privacy Policy', href: 'https://symplelending.com/california-privacy-policy' },
  { label: 'Opt Out', href: 'https://symplelending.com/opt-out' },
  { label: 'E-Comm Consent', href: 'https://symplelending.com/esign-consent' },
  { label: 'Privacy Notice', href: 'https://symplelending.com/privacy-notice' }
];

const SOCIAL_LINKS = [
  { Icon: FaFacebookF, href: 'https://www.facebook.com/symplelending/', label: 'Facebook' },
  { Icon: FaXTwitter, href: 'https://x.com/symplelend/?mx=2', label: 'X' },
  { Icon: FaInstagram, href: 'https://www.instagram.com/symplelending/#', label: 'Instagram' },
  { Icon: FaLinkedinIn, href: 'https://www.linkedin.com/company/symple-lending/', label: 'LinkedIn' },
  { Icon: FaTiktok, href: 'https://www.tiktok.com/@symple_lending', label: 'TikTok' }
];

const NMLS_COMPANY_HREF = 'https://www.nmlsconsumeraccess.org/EntityDetails.aspx/COMPANY/2508833';

const NmlsLink = ({ id, href }: { id: string; href: string }) => (
  <a
    href={href}
    className="text-[#212d52] hover:underline"
    target="_blank"
    rel="noopener noreferrer"
  >
    NMLS {id}
  </a>
);


export default function Footer() {
  return (
    <footer className="bg-white mt-12 border-t border-gray-100">
      <div className="max-w-7xl mx-auto px-4 py-10 sm:px-6 lg:px-8">

        {/* Logo + Social Icons */}
        <div className="flex flex-col-reverse items-center gap-4 mb-6 sm:flex-row sm:justify-between sm:gap-0">
          <img
            src="/symple-lending-logo.png"
            alt="Symple Lending"
            className="h-10"
          />
          <div className="flex items-center gap-4">
            {SOCIAL_LINKS.map(({ Icon, href, label }) => (
              <a
                key={href}
                href={href}
                className="text-gray-600 hover:text-[#212d52] transition-colors"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
              >
                <Icon className="w-5 h-5" />
              </a>
            ))}
          </div>
        </div>

        {/* Legal Links */}
        <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 mb-3 sm:justify-start">
          {LEGAL_LINKS.map((link, index) => (
            <React.Fragment key={link.href}>
              <a
                href={link.href}
                className="text-sm text-gray-600 hover:text-[#212d52] transition-colors"
                target="_blank"
                rel="noopener noreferrer"
              >
                {link.label}
              </a>
              {index < LEGAL_LINKS.length - 1 && (
                <span className="text-gray-300 select-none">|</span>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Copyright + NMLS */}
        <p className="text-sm text-gray-500 mb-8 text-center sm:text-left">
          © {new Date().getFullYear()} Symple Lending, LLC. All rights reserved.{' '}
          <NmlsLink id="#2508833" href={NMLS_COMPANY_HREF} />
        </p>

        {/* Disclosure */}
        <p className="text-sm text-gray-500 max-w-5xl mb-8">
          Symple Lending, LLC is a Utah licensed lender under the Utah Department of Financial
          Institutions (<NmlsLink id="#2508833" href={NMLS_COMPANY_HREF} />). Personal loan offers
          provided to customers who originated via a paid Google or Bing advertisement feature rate
          quotes on Symple Lending of no greater than 35.99% APR with terms from 61 days to 180
          months. Your actual rate depends upon credit score, loan amount, loan term, domicile and
          credit usage and history, and will be agreed upon between you and the lender. An example
          of total amount paid on a personal loan of $10,000 for a term of 36 months at a rate of
          10% would be equivalent to $11,616.12 over the 36 month life of the loan.
        </p>

        {/* Equal Housing Lender */}
        <div className="mb-6">
          <img
            src="/equal-housing-lender.png"
            alt="Equal Housing Lender"
            className="h-12"
          />
        </div>

        {/* Partners Licensing Information */}
        <div className="text-sm text-gray-600">
          <p className="mb-2">
            Symple Lending&apos;s{' '}
            <a
              href="https://symplelending.com/partners"
              className="font-semibold text-[#212d52] hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              Partners
            </a>{' '}
            Licensing Information:
          </p>
          <ul className="list-disc list-inside space-y-1 text-gray-500">
            <li>
              Achieve Personal Loans (
              <NmlsLink
                id="#227977"
                href="https://www.nmlsconsumeraccess.org/EntityDetails.aspx/COMPANY/227977"
              />
              ), HELOC from Achieve Loans (
              <NmlsLink
                id="# 1810501"
                href="https://www.nmlsconsumeraccess.org/EntityDetails.aspx/COMPANY/1810501"
              />
              ).
            </li>
            <li>
              ML Enterprise Inc., a.k.a. Engine by MoneyLion, (
              <NmlsLink
                id="#1475872"
                href="https://www.nmlsconsumeraccess.org/EntityDetails.aspx/COMPANY/1475872"
              />
              )
            </li>
          </ul>
        </div>

      </div>
    </footer>
  );
}